// File: frontend/src/components/MitreHeatmap.tsx
// MITRE ATT&CK Heatmap visualization that replaces the old ATT&CK VIEW.
// Displays the Enterprise matrix with highlighted techniques from the current article.

import React, { useState, useMemo } from 'react';
import useSWR from 'swr';
import { useApi } from '../hooks/useApi';
import {
    Box,
    CircularProgress,
    Alert,
    Typography,
    Paper,
    Tooltip,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { alpha } from '@mui/material';
import { MITRE_ATTACK_ENTERPRISE } from '../data/mitre-attack-enterprise';
import { cyber } from '../theme';
import { HighlightedTechnique } from '../types/mitreHeatmap';

interface MitreHeatmapProps {
    mitreLayerUrl?: string;
    /** Direct technique highlights — skips the layer fetch. Used by entity profiles. */
    directHighlights?: HighlightedTechnique[];
}

/** MITRE Navigator layer JSON structure (subset of fields we need) */
interface MitreLayerTechnique {
    techniqueID: string;
    tactic?: string;
    comment?: string;
    enabled?: boolean;
}

interface MitreLayerJson {
    name?: string;
    techniques?: MitreLayerTechnique[];
}

/**
 * Parse MITRE Navigator layer JSON to extract highlighted techniques.
 * The layer JSON is the same format used by the MITRE ATT&CK Navigator.
 */
function parseLayerToHighlights(layerJson: MitreLayerJson): HighlightedTechnique[] {
    if (!layerJson.techniques) return [];

    return layerJson.techniques
        .filter(t => t.enabled !== false)
        .map(t => ({
            techniqueId: t.techniqueID,
            tactic: t.tactic || '',
            comment: t.comment || '',
        }));
}

/**
 * Build a lookup set for highlighted techniques.
 * Key format: "techniqueId" or "techniqueId|tactic" when tactic is specified.
 */
function buildHighlightLookup(highlights: HighlightedTechnique[]): {
    byTechniqueId: Map<string, HighlightedTechnique[]>;
    specificKeys: Set<string>;
} {
    const byTechniqueId = new Map<string, HighlightedTechnique[]>();
    const specificKeys = new Set<string>();

    for (const h of highlights) {
        // Group by technique ID (a technique can appear in multiple tactics)
        const existing = byTechniqueId.get(h.techniqueId) || [];
        existing.push(h);
        byTechniqueId.set(h.techniqueId, existing);

        if (h.tactic) {
            specificKeys.add(`${h.techniqueId}|${h.tactic}`);
        }
    }

    return { byTechniqueId, specificKeys };
}

/**
 * Check if a technique is highlighted for a specific tactic.
 * Handles both cases: when highlights specify a tactic and when they don't.
 */
function isHighlighted(
    techniqueId: string,
    tacticShortName: string,
    lookup: ReturnType<typeof buildHighlightLookup>
): HighlightedTechnique | null {
    const specificKey = `${techniqueId}|${tacticShortName}`;
    if (lookup.specificKeys.has(specificKey)) {
        const highlights = lookup.byTechniqueId.get(techniqueId) || [];
        return highlights.find(h => h.tactic === tacticShortName) || highlights[0] || null;
    }

    // If no specific tactic match, check if the technique is highlighted without tactic specificity
    const highlights = lookup.byTechniqueId.get(techniqueId);
    if (highlights) {
        const noTacticHighlight = highlights.find(h => !h.tactic);
        if (noTacticHighlight) return noTacticHighlight;
    }

    return null;
}

const MitreHeatmap: React.FC<MitreHeatmapProps> = ({ mitreLayerUrl, directHighlights }) => {
    const { simpleTextFetcher } = useApi();
    const [showOnlyHighlighted, setShowOnlyHighlighted] = useState(false);

    // When directHighlights are provided there is nothing to fetch
    const { data: layerJsonString, error, isLoading } = useSWR(directHighlights ? null : mitreLayerUrl, simpleTextFetcher);

    const { highlights, lookup } = useMemo(() => {
        if (directHighlights) {
            return { highlights: directHighlights, lookup: buildHighlightLookup(directHighlights) };
        }
        if (!layerJsonString) return { highlights: [], lookup: buildHighlightLookup([]) };
        try {
            const layerJson: MitreLayerJson = JSON.parse(layerJsonString);
            const h = parseLayerToHighlights(layerJson);
            return { highlights: h, lookup: buildHighlightLookup(h) };
        } catch {
            return { highlights: [], lookup: buildHighlightLookup([]) };
        }
    }, [layerJsonString, directHighlights]);

    if (!mitreLayerUrl && !directHighlights) {
        return <Alert severity="info">No MITRE ATT&CK layer data available for this report.</Alert>;
    }
    if (!directHighlights && isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
    }
    if (!directHighlights && error) {
        return <Alert severity="error">Could not load MITRE ATT&CK layer data.</Alert>;
    }
    if (highlights.length === 0) {
        return <Alert severity="info">No ATT&CK techniques identified.</Alert>;
    }

    // Count highlighted techniques per tactic
    const highlightCountByTactic = new Map<string, number>();
    for (const tactic of MITRE_ATTACK_ENTERPRISE) {
        let count = 0;
        for (const tech of tactic.techniques) {
            if (isHighlighted(tech.id, tactic.shortName, lookup)) {
                count++;
            }
        }
        highlightCountByTactic.set(tactic.shortName, count);
    }

    // Filter tactics that have at least one highlight when in "only highlighted" mode
    const visibleTactics = showOnlyHighlighted
        ? MITRE_ATTACK_ENTERPRISE.filter(t => (highlightCountByTactic.get(t.shortName) || 0) > 0)
        : MITRE_ATTACK_ENTERPRISE;

    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography variant="h6">
                        MITRE ATT&CK® Heatmap
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {highlights.length} technique{highlights.length !== 1 ? 's' : ''} identified in this report
                    </Typography>
                </Box>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showOnlyHighlighted}
                            onChange={(e) => setShowOnlyHighlighted(e.target.checked)}
                            size="small"
                        />
                    }
                    label={
                        <Typography variant="body2">
                            Only observed techniques
                        </Typography>
                    }
                />
            </Box>

            {/* Scrollable matrix grid */}
            <Box sx={{ overflowX: 'auto', pb: 2 }}>
                <Box sx={{ display: 'flex', gap: 0.5, minWidth: visibleTactics.length * 140 }}>
                    {visibleTactics.map((tactic) => {
                        const highlightCount = highlightCountByTactic.get(tactic.shortName) || 0;
                        const visibleTechniques = showOnlyHighlighted
                            ? tactic.techniques.filter(t => isHighlighted(t.id, tactic.shortName, lookup))
                            : tactic.techniques;

                        return (
                            <Box
                                key={tactic.id}
                                sx={{
                                    flex: '1 0 130px',
                                    maxWidth: 180,
                                    minWidth: 130,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {/* Tactic header */}
                                <Paper
                                    sx={{
                                        p: 1,
                                        textAlign: 'center',
                                        bgcolor: highlightCount > 0 ? alpha(cyber.primary, 0.15) : 'background.paper',
                                        borderBottom: highlightCount > 0 ? `2px solid ${cyber.primary}` : '2px solid transparent',
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            display: 'block',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {tactic.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.6rem',
                                            color: 'text.secondary',
                                            display: 'block',
                                            fontFamily: cyber.fontMono,
                                        }}
                                    >
                                        {tactic.id}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.6rem',
                                            color: highlightCount > 0 ? cyber.primaryLight : 'text.secondary',
                                            fontWeight: highlightCount > 0 ? 600 : 400,
                                        }}
                                    >
                                        {highlightCount}/{tactic.techniques.length}
                                    </Typography>
                                </Paper>

                                {/* Technique cells */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                    {visibleTechniques.map((tech) => {
                                        const highlight = isHighlighted(tech.id, tactic.shortName, lookup);
                                        return (
                                            <Tooltip
                                                key={tech.id}
                                                title={
                                                    highlight ? (
                                                        <Box sx={{ p: 0.5, maxWidth: 300 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {tech.id} — {tech.name}
                                                            </Typography>
                                                            {highlight.comment && (
                                                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                                                    {highlight.comment}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        `${tech.id} — ${tech.name}`
                                                    )
                                                }
                                                arrow
                                                placement="right"
                                            >
                                                <Paper
                                                    sx={{
                                                        px: 0.75,
                                                        py: 0.4,
                                                        fontSize: '0.6rem',
                                                        lineHeight: 1.3,
                                                        cursor: highlight ? 'pointer' : 'default',
                                                        bgcolor: highlight
                                                            ? alpha(cyber.accent.coral, 0.2)
                                                            : 'rgba(255,255,255,0.02)',
                                                        border: highlight
                                                            ? `1px solid ${alpha(cyber.accent.coral, 0.5)}`
                                                            : '1px solid rgba(255,255,255,0.04)',
                                                        transition: 'all 0.15s ease',
                                                        '&:hover': highlight ? {
                                                            bgcolor: alpha(cyber.accent.coral, 0.35),
                                                            borderColor: alpha(cyber.accent.coral, 0.7),
                                                        } : {
                                                            bgcolor: 'rgba(255,255,255,0.05)',
                                                        },
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontSize: 'inherit',
                                                            fontFamily: cyber.fontMono,
                                                            fontWeight: highlight ? 500 : 400,
                                                            color: highlight ? cyber.accent.coral : 'text.secondary',
                                                        }}
                                                    >
                                                        {tech.id}
                                                    </Typography>
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            fontSize: 'inherit',
                                                            ml: 0.5,
                                                            color: highlight ? 'text.primary' : 'text.secondary',
                                                        }}
                                                    >
                                                        {tech.name}
                                                    </Typography>
                                                </Paper>
                                            </Tooltip>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default MitreHeatmap;
